/**
 * Fixed-scale decimal field.
 *
 * `MoneyInput` canonicalizes to the CURRENCY exponent (2 for SAR), but the
 * purchasing wire demands an exact schema scale — quantities and unit costs
 * carry six fraction digits, amounts four. So this field commits through
 * `parseDecimalInput`, which is BigInt-exact and never touches `Number()`.
 * The committed value is always a canonical wire string or "".
 */
import { useEffect, useState } from "react";
import type { InputHTMLAttributes } from "react";
import { Input, cn } from "@erp/ui";

import { parseDecimalInput } from "../decimal";

export interface DecimalInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "onChange" | "type" | "inputMode" | "dir"
> {
  /** Canonical decimal string at `scale`, or "" when empty. */
  value: string;
  /** Exact wire scale: 6 for quantities/unit costs, 4 for amounts. */
  scale: number;
  /** Fired on blur with the canonical string, or "" when cleared. */
  onChange: (next: string) => void;
  /** Fired on blur when the text does not parse; the raw text is kept. */
  onParseError?: (raw: string) => void;
}

export function DecimalInput({
  value,
  scale,
  onChange,
  onParseError,
  className,
  onBlur,
  "aria-invalid": ariaInvalidProp,
  ...rest
}: DecimalInputProps) {
  const [text, setText] = useState(value);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setText(value);
    setInvalid(false);
  }, [value]);

  return (
    <Input
      {...rest}
      // physical-ok: decimal figures are always read left-to-right.
      dir="ltr"
      inputMode="decimal"
      value={text}
      aria-invalid={invalid ? true : ariaInvalidProp}
      className={cn("tabular-nums", className)}
      onChange={(event) => setText(event.currentTarget.value)}
      onBlur={(event) => {
        const raw = event.currentTarget.value;
        if (raw.trim() === "") {
          setInvalid(false);
          if (value !== "") onChange("");
        } else {
          const canonical = parseDecimalInput(raw, scale);
          if (canonical === null) {
            setInvalid(true);
            onParseError?.(raw);
          } else {
            setInvalid(false);
            setText(canonical);
            if (canonical !== value) onChange(canonical);
          }
        }
        onBlur?.(event);
      }}
    />
  );
}
